import { useState, useEffect, useMemo } from "react";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FieldRenderer } from "@/features/inventory/item-detail/components/FieldRenderer";
import type { DocTypeSchema, DocFieldSchema } from "@/lib/erp/meta";
import type { ChildTableRow } from "./types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface ChildRowEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rowData: ChildTableRow;
  onRowChange: (updatedRow: ChildTableRow) => void;
  childMeta: DocTypeSchema;
  disabled?: boolean;
  resetKey?: string | number;
  onDelete?: () => void; // Optional delete handler
  parentDoc?: Record<string, any>; // Parent document for eval:parent.* expressions
}

/**
 * Modal for editing a single child table row with all fields
 * 
 * Follows System View visual rhythm:
 * - Title: "Edit {Child DocType Label}"
 * - Subheader: Key identifiers (read-only)
 * - Two-column grid for normal fields
 * - Full-width for long text/descriptions
 * - Footer: Cancel / Save (optional Delete row)
 */
export function ChildRowEditModal({
  open,
  onOpenChange,
  rowData,
  onRowChange,
  childMeta,
  disabled,
  resetKey,
  onDelete,
  parentDoc,
}: ChildRowEditModalProps) {
  const [localRowData, setLocalRowData] = useState<Record<string, any>>({});

  // Clone row data when modal opens
  useEffect(() => {
    if (open) {
      setLocalRowData({ ...rowData });
    }
  }, [open, rowData]);

  // Get all non-hidden, non-system fields
  const allFields = useMemo(() => {
    return childMeta.fields.filter(
      (f) =>
        f.hidden !== 1 &&
        !f.fieldname.startsWith("__") &&
        !["idx", "docstatus", "__islocal", "name", "parent", "parenttype", "parentfield"].includes(
          f.fieldname
        )
    );
  }, [childMeta]);

  // Compute dependencies for visible fields
  const { visibleFieldNames, fieldReadOnly, fieldRequired } = useFieldDependencies(
    allFields,
    localRowData,
    parentDoc
  );

  // Get key identifier fields (title field or first few important fields) for subheader
  const keyIdentifiers = useMemo(() => {
    const identifiers: DocFieldSchema[] = [];
    
    // Add title field if available
    if (childMeta.title_field) {
      const titleField = allFields.find(f => f.fieldname === childMeta.title_field);
      if (titleField) {
        identifiers.push(titleField);
      }
    }
    
    // Add first Link or Data field if no title field
    if (identifiers.length === 0) {
      const firstKeyField = allFields.find(
        f => ['Link', 'Data'].includes(f.fieldtype) && f.fieldname !== 'name'
      );
      if (firstKeyField) {
        identifiers.push(firstKeyField);
      }
    }
    
    return identifiers;
  }, [allFields, childMeta.title_field]);

  // Handle field changes
  const handleFieldChange = (fieldname: string, value: any) => {
    setLocalRowData((prev) => ({
      ...prev,
      [fieldname]: value,
    }));
  };

  // Handle save
  const handleSave = () => {
    // Apply all local changes to parent row data
    onRowChange(localRowData as ChildTableRow);
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset to original data
    setLocalRowData({ ...rowData });
    onOpenChange(false);
  };

  // Separate fields into normal fields and long text fields
  const normalFields = useMemo(() => {
    return allFields.filter(
      (f) =>
        visibleFieldNames.has(f.fieldname) &&
        !['Text Editor', 'Long Text', 'Small Text'].includes(f.fieldtype)
    );
  }, [allFields, visibleFieldNames]);

  const longTextFields = useMemo(() => {
    return allFields.filter(
      (f) =>
        visibleFieldNames.has(f.fieldname) &&
        ['Text Editor', 'Long Text', 'Small Text'].includes(f.fieldtype)
    );
  }, [allFields, visibleFieldNames]);

  // Get language for translations
  const { language } = useLanguage();
  
  // Get translated UI labels
  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      edit: getUI("edit", "Edit"),
      deleteRow: getUI("delete_row", "Delete Row"),
      noFieldsAvailable: getUI("no_fields_available", "No fields available to edit"),
      cancel: getUI("cancel", "Cancel"),
      save: getUI("modal_save", "Save"),
    };
  }, [language]);
  
  // Get translated doctype name (handles both singular and plural)
  const getDoctypeTranslation = (doctypeName: string): string => {
    // Normalize doctype name to key format
    const normalized = doctypeName.toLowerCase().replace(/\s+/g, "_");
    
    // Try exact match first
    let translation = getUITranslation(`doctype_${normalized}`, language);
    if (translation) return translation;
    
    // Try plural form (add 's' or 'es')
    const pluralKey = normalized.endsWith('s') ? normalized : `${normalized}s`;
    translation = getUITranslation(`doctype_${pluralKey}`, language);
    if (translation) return translation;
    
    // Try common variations
    if (normalized === 'serial_no') {
      translation = getUITranslation('doctype_serial_nos', language);
      if (translation) return translation;
    }
    if (normalized === 'batch') {
      translation = getUITranslation('doctype_batches', language);
      if (translation) return translation;
    }
    
    // Fallback to original name
    return doctypeName;
  };

  // Get child doctype label (use name or fallback)
  const childDoctypeName = childMeta.name || 'Row';
  const childDoctypeLabel = getDoctypeTranslation(childDoctypeName);
  const modalTitle = `${uiLabels.edit} ${childDoctypeLabel}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
          {keyIdentifiers.length > 0 && (
            <DialogDescription className="space-y-1">
              {keyIdentifiers.map((field) => (
                <div key={field.fieldname} className="text-sm">
                  <span className="text-muted-foreground">{field.label}:</span>{" "}
                  <span className="font-medium">
                    {localRowData[field.fieldname] || "-"}
                  </span>
                </div>
              ))}
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleSave(); }}>
          <div className="space-y-6 px-6 py-4">
            {/* Two-column grid for normal fields */}
            {normalFields.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {normalFields.map((field) => (
                  <FieldRenderer
                    key={field.fieldname}
                    field={field}
                    value={localRowData[field.fieldname]}
                    onChange={(value) => handleFieldChange(field.fieldname, value)}
                    disabled={disabled || fieldReadOnly[field.fieldname]}
                    isReadOnly={fieldReadOnly[field.fieldname]}
                    isRequired={fieldRequired[field.fieldname]}
                    resetKey={resetKey}
                  />
                ))}
              </div>
            )}

            {/* Full-width for long text fields */}
            {longTextFields.length > 0 && (
              <div className="space-y-6">
                {longTextFields.map((field) => (
                  <FieldRenderer
                    key={field.fieldname}
                    field={field}
                    value={localRowData[field.fieldname]}
                    onChange={(value) => handleFieldChange(field.fieldname, value)}
                    disabled={disabled || fieldReadOnly[field.fieldname]}
                    isReadOnly={fieldReadOnly[field.fieldname]}
                    isRequired={fieldRequired[field.fieldname]}
                    resetKey={resetKey}
                  />
                ))}
              </div>
            )}

            {normalFields.length === 0 && longTextFields.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                {uiLabels.noFieldsAvailable}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 pb-6">
            <div className="flex items-center justify-between w-full">
              {/* Left: Optional Delete button */}
              {onDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    onDelete();
                    onOpenChange(false);
                  }}
                  disabled={disabled}
                >
                  {uiLabels.deleteRow}
                </Button>
              )}
              {!onDelete && <div />}

              {/* Right: Cancel / Save */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={disabled}
                >
                  {uiLabels.cancel}
                </Button>
                <Button
                  type="submit"
                  disabled={disabled}
                >
                  {uiLabels.save}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

